// features/workstations/tabs/tools.js
// ═══════════════════════════════════════════════════════════
// TOOLS TAB — REI calculator suite for the Real Estate / Wealth
// Building workstation. Gated to wealth-building workstations in
// config-schema.js (alongside Deals + Close).
//
// Hub (grid by category) → a calculator (inputs → live outputs)
// → disclosed lender comparisons (the affiliate / options-panel
// monetization; see DEAL_INTELLIGENCE_DOMAIN.md partner map).
//
// Config-driven: all calculators live in
// lib/wealth-building/tools/calculators.js.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  CALCULATORS, CALCULATORS_BY_ID, TOOL_CATEGORIES,
  LENDER_COMPARISONS, DISCLOSURE,
} from '../../../lib/wealth-building/tools/calculators';

const C = {
  gold: '#c8a96e', ink: '#ececf2', inkSoft: '#a6a6b8', inkFaint: '#76768a',
  surface: '#111118', surface2: '#171723', border: '#272736',
  pos: '#7a9e82', neg: '#c46050',
};

export default function ToolsTab() {
  const [view, setView] = useState({ type: 'hub' });

  return (
    <div className="ws-tab-content" style={{ overflowY: 'auto', height: '100%' }}>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '28px 24px 80px' }}>
        {view.type === 'hub' && <Hub onOpen={setView} />}
        {view.type === 'calc' && (
          <CalculatorPanel calc={CALCULATORS_BY_ID[view.id]} onBack={() => setView({ type: 'hub' })} onLender={(key) => setView({ type: 'lender', key })} />
        )}
        {view.type === 'lender' && (
          <LenderPanel data={LENDER_COMPARISONS[view.key]} onBack={() => setView({ type: 'hub' })} />
        )}
      </div>
    </div>
  );
}

// ── Hub ─────────────────────────────────────────────────────
function Hub({ onOpen }) {
  return (
    <>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: C.gold, marginBottom: 6 }}>Workstation</div>
      <h1 style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-.01em', color: C.ink, margin: '0 0 6px' }}>Tools</h1>
      <p style={{ fontSize: 15, color: C.inkSoft, margin: '0 0 24px', maxWidth: 560 }}>
        The numbers behind every deal — run them in seconds. ARV, the 70% rule, rehab, rental returns, financing, and carry.
      </p>

      {TOOL_CATEGORIES.map((cat) => {
        const items = CALCULATORS.filter((c) => c.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat} style={{ marginBottom: 26 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.inkFaint, marginBottom: 12 }}>{cat}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
              {items.map((c) => (
                <button key={c.id} onClick={() => onOpen({ type: 'calc', id: c.id })}
                  style={{ textAlign: 'left', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '15px 16px', cursor: 'pointer', transition: 'border-color .15s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.gold)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink, marginBottom: 3 }}>{c.title}</div>
                  <div style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.45 }}>{c.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.inkFaint, marginBottom: 12 }}>Financing partners</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
          {Object.entries(LENDER_COMPARISONS).map(([key, l]) => (
            <button key={key} onClick={() => onOpen({ type: 'lender', key })}
              style={{ textAlign: 'left', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '15px 16px', cursor: 'pointer' }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.gold)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: C.ink, marginBottom: 3 }}>{l.title}</div>
              <div style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.45 }}>{l.blurb}</div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Calculator panel ────────────────────────────────────────
function CalculatorPanel({ calc, onBack, onLender }) {
  const init = {};
  calc.inputs.forEach((i) => { init[i.key] = i.def; });
  const [vals, setVals] = useState(init);

  const set = (key, raw, type) => {
    const v = type === 'select' ? raw : (raw === '' ? '' : parseFloat(raw));
    setVals((p) => ({ ...p, [key]: v }));
  };
  const result = calc.compute(
    Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, v === '' || v === undefined ? 0 : v]))
  );
  const showLender = calc.category === 'Financing' || calc.category === 'Rental';

  return (
    <>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.inkFaint, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 16 }}>← All tools</button>
      <h1 style={{ fontSize: 23, fontWeight: 800, color: C.ink, margin: '0 0 5px' }}>{calc.title}</h1>
      <p style={{ fontSize: 14, color: C.inkSoft, margin: '0 0 22px' }}>{calc.blurb}</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22, alignItems: 'start' }}>
        {/* inputs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {calc.inputs.map((i) => (
            <label key={i.key} style={{ display: 'block' }}>
              <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: C.ink, marginBottom: 5 }}>
                {i.label}{i.suffix ? <span style={{ color: C.inkFaint, fontWeight: 400 }}> ({i.suffix})</span> : null}
              </span>
              {i.type === 'select' ? (
                <select value={vals[i.key]} onChange={(e) => set(i.key, e.target.value, 'select')}
                  style={inputStyle}>
                  {i.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <div style={{ position: 'relative' }}>
                  {i.prefix && <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.inkFaint, fontSize: 14 }}>{i.prefix}</span>}
                  <input type="number" value={vals[i.key]} onChange={(e) => set(i.key, e.target.value)}
                    style={{ ...inputStyle, paddingLeft: i.prefix ? 24 : 12 }} />
                </div>
              )}
            </label>
          ))}
        </div>

        {/* outputs */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px' }}>
          {result.outputs.map((o, idx) => (
            <div key={idx} style={{ padding: '10px 0', borderBottom: idx < result.outputs.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: 11.5, color: C.inkFaint, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>{o.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.01em', color: o.tone === 'pos' ? C.pos : o.tone === 'neg' ? C.neg : C.ink }}>{o.value}</div>
            </div>
          ))}
          {result.note && <p style={{ fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5, marginTop: 12, marginBottom: 0 }}>{result.note}</p>}
        </div>
      </div>

      {showLender && (
        <div style={{ marginTop: 20, fontSize: 13 }}>
          <span style={{ color: C.inkFaint }}>Need financing? </span>
          <button onClick={() => onLender(calc.id === 'dscr' || calc.id === 'rental' || calc.id === 'refinance' ? 'dscr' : 'hard-money')}
            style={{ background: 'none', border: 'none', color: C.gold, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
            Compare {calc.id === 'dscr' || calc.id === 'rental' || calc.id === 'refinance' ? 'DSCR' : 'hard money'} lenders →
          </button>
        </div>
      )}
    </>
  );
}

// ── Lender comparison ───────────────────────────────────────
function LenderPanel({ data, onBack }) {
  return (
    <>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.inkFaint, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 16 }}>← All tools</button>
      <h1 style={{ fontSize: 23, fontWeight: 800, color: C.ink, margin: '0 0 5px' }}>{data.title}</h1>
      <p style={{ fontSize: 14, color: C.inkSoft, margin: '0 0 22px' }}>{data.blurb}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {data.partners.map((p) => (
          <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 18px' }}>
            <div>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: C.ink }}>{p.name}</div>
              <div style={{ fontSize: 13, color: C.gold, marginTop: 2 }}>{p.highlight}</div>
              <div style={{ fontSize: 12.5, color: C.inkSoft, marginTop: 2 }}>{p.note}</div>
            </div>
            <button onClick={(e) => e.preventDefault()} style={{ flexShrink: 0, background: C.gold, color: '#1a1a12', border: 'none', borderRadius: 9, padding: '10px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>Apply →</button>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 11.5, color: C.inkFaint, marginTop: 14, lineHeight: 1.5 }}>{DISCLOSURE}</p>
    </>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 9,
  fontSize: 14, fontFamily: 'inherit', color: C.ink, background: C.surface2, boxSizing: 'border-box',
};
